import { useEffect, useState } from "react";
import { Megaphone, Send, Loader2, Users, ShoppingBag, UserPlus, Mail, CheckCircle2 } from "lucide-react";
import { api, fmtError } from "../lib/api";
import { toast } from "sonner";

const AUDIENCE_ICONS = { artists: Users, buyers: ShoppingBag, registered: UserPlus, newsletter: Mail };

const TEMPLATES = [
  { v: "", label: "Message libre (vide)" },
  {
    v: "nouveautes", label: "Modèle : Nouveautés du catalogue",
    subject: "De nouvelles musiques viennent d'arriver",
    message: "Le catalogue s'enrichit de nouveaux morceaux prêts pour vos images.\n\nRendez-vous sur laconsoledesartistes.fr/catalogue pour les écouter : chaque titre est disponible en licence immédiate, avec téléchargement du master après paiement.\n\nBonne écoute !",
  },
  {
    v: "annonce", label: "Modèle : Annonce",
    subject: "Une information de La Console des Artistes",
    message: "Nous vous écrivons au sujet de [précisez ici].\n\nN'hésitez pas à répondre directement à cet email si vous avez la moindre question — nous vous lisons.\n\nÀ très vite,",
  },
  {
    v: "offre", label: "Modèle : Rappel des remises",
    subject: "Jusqu'à −10 % sur vos licences musicales",
    message: "Petit rappel utile pour vos prochains projets : les remises sont automatiques au panier.\n\n−5 % dès 2 morceaux, −7 % dès 3 morceaux, −10 % dès 5 morceaux.\n\nProfitez-en sur laconsoledesartistes.fr/catalogue !",
  },
];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

export const AdminBroadcast = () => {
  const [audiences, setAudiences] = useState(null);
  const [audience, setAudience] = useState("artists");
  const [template, setTemplate] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const load = () => {
    api.get("/admin/audiences").then((r) => setAudiences(r.data.audiences)).catch(() => {});
    api.get("/admin/broadcasts").then((r) => setHistory(r.data.broadcasts)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  // rafraîchit la progression des envois en cours
  useEffect(() => {
    if (!history.some((b) => b.status === "sending")) return;
    const t = setInterval(() => {
      api.get("/admin/broadcasts").then((r) => setHistory(r.data.broadcasts)).catch(() => {});
    }, 5000);
    return () => clearInterval(t);
  }, [history]);

  const applyTemplate = (v) => {
    setTemplate(v);
    const t = TEMPLATES.find((x) => x.v === v);
    if (t && t.v) { setSubject(t.subject); setMessage(t.message); }
  };

  const send = async () => {
    const count = audiences?.[audience]?.count || 0;
    if (!window.confirm(`Envoyer cet email à ${count} destinataire(s) « ${audiences?.[audience]?.label} » ?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/broadcast", { audience, subject, message });
      toast.success(`Envoi lancé vers ${r.data.total} destinataire(s) — bilan dans la cloche à la fin`);
      setSubject(""); setMessage(""); setTemplate("");
      load();
    } catch (e) { toast.error(fmtError(e)); } finally { setBusy(false); }
  };

  const inputCls = "w-full border border-white/15 bg-transparent px-4 py-3 text-sm outline-none transition-colors focus:border-amber-500";
  const labelCls = "mb-2 block text-[10px] uppercase tracking-[0.25em] text-zinc-500";

  return (
    <section className="border border-white/[0.08] bg-surface p-8" data-testid="admin-broadcast-section">
      <h2 className="flex items-center gap-3 font-display text-2xl font-medium">
        <Megaphone className="h-5 w-5 text-amber-500" /> Envois groupés
      </h2>
      <p className="mt-3 text-sm leading-relaxed text-zinc-400">
        Envoyez un email à toute une audience. Chaque personne reçoit un email individuel depuis
        contact@laconsoledesartistes.fr — les abonnés newsletter ont un lien de désinscription.
      </p>

      <label className={`${labelCls} mt-6`}>1. Choisir l'audience</label>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="broadcast-audiences">
        {audiences && Object.entries(audiences).map(([key, a]) => {
          const Icon = AUDIENCE_ICONS[key] || Users;
          const active = audience === key;
          return (
            <button key={key} onClick={() => setAudience(key)} data-testid={`audience-${key}`}
                    className={`border p-4 text-left transition-colors ${active ? "border-amber-500 bg-amber-500/10" : "border-white/10 hover:border-white/30"}`}>
              <Icon className={`h-4 w-4 ${active ? "text-amber-400" : "text-zinc-500"}`} />
              <p className="mt-2 text-xs font-bold uppercase tracking-[0.15em] text-zinc-200">{a.label}</p>
              <p className="mt-1 font-display text-2xl font-medium text-amber-500" data-testid={`audience-count-${key}`}>{a.count}</p>
            </button>
          );
        })}
      </div>

      <label className={`${labelCls} mt-6`}>2. Rédiger (ou partir d'un modèle)</label>
      <select value={template} onChange={(e) => applyTemplate(e.target.value)} data-testid="broadcast-template"
              className="mb-4 w-full border border-white/15 bg-black px-4 py-3 text-sm outline-none focus:border-amber-500 sm:w-auto">
        {TEMPLATES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
      </select>
      <div className="space-y-4">
        <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="broadcast-subject"
               className={inputCls} placeholder="Objet de l'email" maxLength={150} />
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={7} data-testid="broadcast-message"
                  className={inputCls} placeholder="Votre message… (les retours à la ligne sont conservés)" />
      </div>

      <button onClick={send} disabled={busy || !subject.trim() || message.trim().length < 10 || !(audiences?.[audience]?.count > 0)}
              data-testid="broadcast-send"
              className="mt-5 flex items-center gap-2 bg-amber-500 px-6 py-3 text-xs font-bold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-400 disabled:opacity-40">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        Envoyer à {audiences?.[audience]?.count ?? "…"} destinataire(s)
      </button>

      {history.length > 0 && (
        <div className="mt-8" data-testid="broadcast-history">
          <p className={labelCls}>Historique des envois</p>
          <div className="space-y-2">
            {history.map((b) => (
              <div key={b.broadcast_id} data-testid={`broadcast-${b.broadcast_id}`}
                   className="flex flex-wrap items-center justify-between gap-2 border border-white/[0.06] px-4 py-3 text-sm">
                <div className="min-w-0">
                  <p className="truncate font-medium text-zinc-200">{b.subject}</p>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-600">{b.audience_label} · {fmtDate(b.created_at)}</p>
                </div>
                <p className={`flex items-center gap-2 text-xs ${b.status === "done" ? "text-emerald-400" : "text-amber-400"}`}
                   data-testid={`broadcast-status-${b.broadcast_id}`}>
                  {b.status === "done" ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {b.sent}/{b.total} envoyés
                  {b.failed ? (
                    <span title={b.failed_emails?.length ? `Adresses en échec : ${b.failed_emails.join(", ")}` : ""}
                          className="cursor-help border-b border-dashed border-red-400/50 text-red-400">
                      {` · ${b.failed} échec(s)`}
                    </span>
                  ) : null}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};
